import React, { useEffect, useMemo, useState } from 'react';
import { fetchAtHomeSupport, fetchSupportBankResourceBlob, fetchSupportBankResourceFiles } from '../services/api';
import { fetchFamilyDescriptions } from '../utils/familyDescriptions';
import { fetchVocabBank, HighlightVocabText } from '../utils/vocabBank';
import ActionCardDocuments from './ActionCardDocuments';
import ActionCardFlag from './ActionCardFlag';
import { getActionCardActivities } from '../utils/actionCardActivities';

const normalizeName = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
const isUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());
const comingSoonStyle = { minHeight: 88, display: 'grid', placeItems: 'center', borderRadius: 8, background: '#c4d6f1', color: '#64748b', fontSize: 12, fontWeight: 700 };

function parseVideoUrl(value) {
  if (!isUrl(value)) return null;
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function getStatusStyle(status) {
  if (status === 'mastered') return { label: 'Proficient', background: 'var(--ae-status-mastered)', color: '#fff', border: 'var(--ae-status-mastered)' };
  if (status === 'on_track') return { label: 'On Track', background: '#d5f5e2', color: '#166534', border: '#7DDDA5' };
  if (status === 'working') return { label: 'Learning Zone', background: '#fef3c7', color: '#92400e', border: '#fcd34d' };
  if (status === 'not_yet') return { label: 'Not Yet', background: 'var(--ae-status-not-yet)', color: '#fff', border: 'var(--ae-status-not-yet)' };
  return { label: 'Not Assessed', background: '#e5e7eb', color: '#475569', border: '#cbd5e1' };
}

function getVideoEmbedUrl(url) {
  if (!url) return '';
  const host = url.hostname.toLowerCase();
  let videoId = '';
  if (host === 'youtu.be') {
    videoId = url.pathname.slice(1);
  } else if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'www.youtube-nocookie.com'].includes(host)) {
    videoId = url.pathname === '/watch' ? url.searchParams.get('v') : url.pathname.match(/^\/(?:embed|shorts)\/([^/]+)$/)?.[1];
  }
  return /^[A-Za-z0-9_-]{11}$/.test(videoId || '') ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1` : '';
}

export default function InlineAtHomeSupportPanel({ studentId, classId, standard, grade, subject, strand, domain, status, useFamilySupport = false, familySupport = null, supportEntry = null, canManageSchoolResources = false, showStudentStatus = true, schoolWideResources = false }: any) {
  const [entries, setEntries] = useState<any>([]);
  const [resourceFiles, setResourceFiles] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [resourceError, setResourceError] = useState<any>('');
  const [localFamilySupport, setLocalFamilySupport] = useState<any>(null);
  const [vocabBank, setVocabBank] = useState<any>(null);

  useEffect(() => {
    if (!useFamilySupport || !subject) return undefined;
    let cancelled = false;
    fetchVocabBank(subject)
      .then((bank) => {
        if (!cancelled) setVocabBank(bank);
      })
      .catch((err) => {
        if (!cancelled) console.error('Could not load vocab bank in support panel:', err);
      });
    return () => { cancelled = true; };
  }, [useFamilySupport, subject]);

  useEffect(() => {
    if (!useFamilySupport) {
      let cancelled = false;
      setLoading(true);
      setError('');
      if (supportEntry) {
        setLoading(false);
      } else {
        fetchAtHomeSupport({ grade, subject, strand, standard })
          .then((data) => {
            if (!cancelled) setEntries(Array.isArray(data?.entries) ? data.entries : []);
          })
          .catch(() => {
            if (!cancelled) setError('At-home support resources could not be loaded.');
          })
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
      }
      fetchSupportBankResourceFiles()
        .then((data) => {
          if (!cancelled) setResourceFiles(Array.isArray(data?.files) ? data.files : []);
        })
        .catch(() => {
          if (!cancelled) setResourceError('Some digital resources may not be available right now.');
        });
      return () => {
        cancelled = true;
      };
    }

    if (!familySupport && standard) {
      let cancelled = false;
      fetchFamilyDescriptions()
        .then((dict) => {
          if (cancelled) return;
          const target = normalizeName(standard);
          const match = dict[standard] || Object.entries(dict || {}).find(([k]) => normalizeName(k) === target)?.[1];
          if (match) setLocalFamilySupport(match);
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [grade, subject, strand, standard, useFamilySupport, familySupport, supportEntry]);

  const activeFamilySupport = familySupport || localFamilySupport;

  const selectedEntry = useMemo(() => {
    if (useFamilySupport) {
      return {
        friendly: activeFamilySupport?.standard || '',
        video_url: activeFamilySupport?.video || '',
        activity: activeFamilySupport?.activity || '',
      };
    }
    if (supportEntry) return supportEntry;
    const wanted = normalizeName(standard);
    return entries.find((entry) => {
      const candidate = normalizeName(entry.standard);
      return candidate === wanted || candidate.startsWith(wanted) || wanted.startsWith(candidate);
    }) || entries[0] || null;
  }, [entries, standard, useFamilySupport, activeFamilySupport, supportEntry]);

  const downloadableFiles = useMemo(() => {
    const map = new Map();
    resourceFiles.forEach((file) => {
      [file.filename, file.label].forEach((value) => {
        const key = normalizeName(value);
        if (key && !map.has(key)) map.set(key, file.filename);
      });
    });
    return map;
  }, [resourceFiles]);

  const openResource = async (name) => {
    try {
      const { blob, filename } = await fetchSupportBankResourceBlob(name);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || name;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    } catch (_error: any) {
      setResourceError(`Could not open digital resource: ${name}`);
    }
  };

  const digitalResources = String(selectedEntry?.digital || '').split('\n').map((item) => item.trim()).filter(Boolean);
  const activityDocuments = getActionCardActivities(standard);
  const videoValue = String(selectedEntry?.video_url || selectedEntry?.video || '').trim();
  const videoComingSoon = /^coming\s+soon(?:[.!…]*)?$/i.test(videoValue);
  const videoUrl = parseVideoUrl(videoValue);
  const videoEmbedUrl = getVideoEmbedUrl(videoUrl);
  const statusStyle = getStatusStyle(status);

  const effectiveSubject = subject || activeFamilySupport?.subject || '';
  const effectiveDomain = domain || activeFamilySupport?.domainName || activeFamilySupport?.domain || '';
  const effectiveStrand = strand || activeFamilySupport?.strandName || activeFamilySupport?.strand || '';
  const effectiveGrade = grade || activeFamilySupport?.grade || '';

  return (
    <section style={{ marginTop: 10, padding: 14, background: '#eef3fb', border: '1px solid #b8cae5', borderRadius: 12, boxShadow: '0 6px 16px rgba(15, 23, 42, 0.06)' }} aria-label="At-home support resources">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: '#1e3a5f', fontSize: 14, fontWeight: 800 }}>{useFamilySupport ? 'Guided Action' : 'Action Card'}</div>
          <div style={{ marginTop: 3, color: '#475569', fontSize: 12, overflowWrap: 'anywhere' }}>
            {effectiveSubject}{effectiveDomain ? ` · ${effectiveDomain}` : ''}{effectiveStrand ? ` · ${effectiveStrand}` : ''}{effectiveGrade ? ` · Grade ${effectiveGrade}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
          {showStudentStatus && (
            <span style={{ flex: '0 0 auto', padding: '6px 10px', border: `1px solid ${statusStyle.border}`, borderRadius: 999, background: statusStyle.background, color: statusStyle.color, fontSize: 11, fontWeight: 800 }}>
              Student progress: {statusStyle.label}
            </span>
          )}
          <ActionCardFlag
            studentId={studentId}
            standardCode={standard}
            subject={effectiveSubject}
            grade={effectiveGrade}
            domain={effectiveDomain}
            strand={effectiveStrand}
          />
        </div>
      </div>

      {!useFamilySupport && loading ? (
        <div style={{ padding: 22, textAlign: 'center', color: '#64748b', background: '#fff', borderRadius: 10 }}>Loading resources…</div>
      ) : !useFamilySupport && error ? (
        <div style={{ padding: 14, color: '#991b1b', background: '#fff', borderRadius: 10 }}>{error}</div>
      ) : !selectedEntry && !activityDocuments.length ? (
        <div style={{ padding: 14, color: '#1e3a5f', background: '#fff', borderRadius: 10 }}>
          <div style={{ fontWeight: 800, marginBottom: 4 }}>Coming Soon</div>
          <div style={{ fontSize: 12 }}>At-home support for this standard is being prepared.</div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 12, padding: 14, background: '#d7e4f8', borderRadius: 10 }}>
            <div style={{ marginBottom: 6, color: '#1e3a8a', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>Learning Outcome</div>
            {selectedEntry?.friendly || selectedEntry?.strand ? (
              <div style={{ color: '#1f2937', fontSize: 13, lineHeight: 1.55 }}>
                {useFamilySupport ? (
                  <HighlightVocabText
                    text={selectedEntry.friendly || selectedEntry.strand}
                    subject={subject}
                    vocabBank={vocabBank}
                    active={useFamilySupport}
                  />
                ) : (
                  selectedEntry.friendly || selectedEntry.strand
                )}
              </div>
            ) : (
              <div style={{ color: '#64748b', fontSize: 12, fontWeight: 700 }}>Coming Soon</div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 12 }}>
            <div style={{ minWidth: 0, padding: 12, background: '#d2deef', borderRadius: 10 }}>
              <div style={{ marginBottom: 8, color: '#1e3a8a', fontSize: 13, fontWeight: 800 }}>Video</div>
              {videoEmbedUrl ? (
                <div style={{ aspectRatio: '16 / 9', overflow: 'hidden', borderRadius: 8, background: '#c4d6f1' }}>
                  <iframe title="At-home support video" src={videoEmbedUrl} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ width: '100%', height: '100%', border: 0 }} />
                </div>
              ) : videoUrl ? (
                <a href={videoUrl.href} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', padding: '8px 10px', borderRadius: 8, background: '#fff', color: '#1d4ed8', fontSize: 12, fontWeight: 700 }}>Open video resource ↗</a>
              ) : videoComingSoon || !videoValue ? (
                <div style={comingSoonStyle}>Coming Soon</div>
              ) : (
                <div style={{ color: '#991b1b', fontSize: 12 }}>The video link is unavailable.</div>
              )}
            </div>

            <div style={{ minWidth: 0, padding: 12, background: '#d2deef', borderRadius: 10 }}>
              <div style={{ marginBottom: 8, color: '#1e3a8a', fontSize: 13, fontWeight: 800 }}>{useFamilySupport ? 'Activity' : 'Digital Resources'}</div>
              {useFamilySupport ? (
                selectedEntry.activity ? (
                  <div style={{ padding: '10px 12px', background: '#fff', borderRadius: 8, color: '#1f2937', fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{selectedEntry.activity}</div>
                ) : !activityDocuments.length ? (
                  <div style={comingSoonStyle}>Coming Soon</div>
                ) : null
              ) : digitalResources.length ? (
                <div style={{ display: 'grid', gap: 7 }}>
                  {digitalResources.map((resource, index) => {
                    if (isUrl(resource)) return <a key={`${resource}-${index}`} href={resource} target="_blank" rel="noreferrer" style={{ padding: '8px 10px', background: '#fff', borderRadius: 8, color: '#1d4ed8', fontSize: 12, overflowWrap: 'anywhere' }}>{resource}</a>;
                    const filename = downloadableFiles.get(normalizeName(resource));
                    return filename ? (
                      <button key={`${resource}-${index}`} type="button" onClick={() => openResource(filename)} style={{ padding: '8px 10px', textAlign: 'left', background: '#fff', border: '1px solid #bfd0e6', borderRadius: 8, color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{resource} ↓</button>
                    ) : (
                      <div key={`${resource}-${index}`} style={{ padding: '8px 10px', background: '#fff', borderRadius: 8, color: '#64748b', fontSize: 12, overflowWrap: 'anywhere' }}>
                        {resource} · <strong>Coming Soon</strong>
                      </div>
                    );
                  })}
                </div>
              ) : !activityDocuments.length ? (
                <div style={comingSoonStyle}>Coming Soon</div>
              ) : null}
              {activityDocuments.length > 0 && (
                <div style={{ display: 'grid', gap: 8, marginTop: 8 }} aria-label="Word bank activities">
                  {activityDocuments.map(({ filename, label, href }) => (
                    <a key={filename} href={href} download={filename} style={{ display: 'block', padding: '10px 12px', background: '#fff', border: '1px solid #bfd0e6', borderRadius: 8, color: '#1d4ed8', fontSize: 13, fontWeight: 700, overflowWrap: 'anywhere' }}>
                      Download {label} (.docx)
                    </a>
                  ))}
                </div>
              )}
              {!useFamilySupport && resourceError && <div style={{ marginTop: 8, color: '#92400e', fontSize: 11 }}>{resourceError}</div>}
              <ActionCardDocuments
                studentId={studentId}
                standardCode={standard}
                canManage={canManageSchoolResources}
                schoolWide={schoolWideResources}
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
}
